import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="chat-input"
export default class extends Controller {
  static targets = ["textarea"]
  static values = {
    scrollAnchor: String,
    conversationId: String
  }

  connect() {
    this.restoreDraft()
    this.autoResize();

    // Focus the textarea when the controller connects
    if (this.hasTextareaTarget) {
      this.textareaTarget.focus();
    }

    // Set up form submission handler to scroll to anchor
    this.boundScrollToAnchor = this.scrollToAnchor.bind(this)
    this.boundClearDraft = this.clearDraft.bind(this)
    this.element.addEventListener('turbo:submit-end', this.boundScrollToAnchor);
    this.element.addEventListener('turbo:submit-end', this.boundClearDraft);
  }

  disconnect() {
    this.element.removeEventListener('turbo:submit-end', this.boundScrollToAnchor)
    this.element.removeEventListener('turbo:submit-end', this.boundClearDraft)
  }

  autoResize() {
    if (!this.hasTextareaTarget) return;
    
    const textarea = this.textareaTarget;
    
    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = 'auto';
    
    // Set the height to match the content, with a max height
    const newHeight = Math.min(textarea.scrollHeight, 100);
    textarea.style.height = `${newHeight}px`;
  }

  handleKeydown(event) {
    // Skip if we don't have the textarea
    if (!this.hasTextareaTarget) return;
    
    // On desktop devices
    if (!this.isMobileDevice()) {
      // If Enter is pressed without Shift key
      if (event.key === 'Enter' && !event.shiftKey) {
        // If Alt key is pressed with Enter, allow new line (don't prevent default)
        if (event.altKey) {
          return; // Allow default behavior (new line)
        }
        
        // Otherwise, prevent default behavior and submit if not empty
        event.preventDefault();
        
        if (this.textareaTarget.value.trim() !== '' || this.hasFiles()) {
          // Find the form and safely submit it
          const form = this.element.closest('form');
          if (form) {
            form.requestSubmit();
          } else {
            console.warn('Form not found for submission');
            // Try to find the submit button as a fallback
            const submitButton = this.element.querySelector('button[type="submit"]');
            if (submitButton) {
              submitButton.click();
            }
          }
        }
      }
    }
    // On mobile devices, let the submit button handle submission
  }
  
  // Helper method to detect mobile devices
  isMobileDevice() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }
  
  hasFiles() {
    const form = this.element.closest('form') || this.element
    return Array.from(form.querySelectorAll('input[type="hidden"][name="chat_message[files][]"]')).some(i => i.value)
  }

  // --- Draft persistence ---

  get draftKey() {
    return `nook_draft_${this.conversationIdValue}`
  }

  restoreDraft() {
    if (!this.hasConversationIdValue || !this.hasTextareaTarget) return
    try {
      const draft = localStorage.getItem(this.draftKey)
      if (draft) {
        this.textareaTarget.value = draft
        // Trigger submit-btn toggle so button state matches restored text
        this.textareaTarget.dispatchEvent(new Event('input', { bubbles: true }))
      }
    } catch (_) { /* localStorage unavailable */ }
  }

  saveDraft() {
    if (!this.hasConversationIdValue || !this.hasTextareaTarget) return
    clearTimeout(this._draftTimer)
    this._draftTimer = setTimeout(() => {
      try {
        const value = this.textareaTarget.value
        if (value) {
          localStorage.setItem(this.draftKey, value)
        } else {
          localStorage.removeItem(this.draftKey)
        }
      } catch (_) { /* localStorage unavailable */ }
    }, 500)
  }

  clearDraft(event) {
    if (!event.detail.success) return
    try {
      // One conversation per user, so clear all draft keys to avoid leaks
      Object.keys(localStorage)
        .filter(k => k.startsWith("nook_draft_"))
        .forEach(k => localStorage.removeItem(k))
    } catch (_) { /* localStorage unavailable */ }
  }

  // Scroll to the specified anchor after form submission
  scrollToAnchor() {
    // Check if we have a scroll anchor value
    if (!this.hasScrollAnchorValue) {
      console.log('No scroll anchor specified');
      return;
    }
    
    // Wait a moment for the DOM to update with the new message
    setTimeout(() => {
      const anchor = document.getElementById(this.scrollAnchorValue);
      if (anchor) {
        // Scroll to the anchor element
        anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
        console.log(`Scrolled to anchor: ${this.scrollAnchorValue}`);
      } else {
        console.log(`Anchor not found: ${this.scrollAnchorValue}`);
      }
    }, 300); // Small delay to ensure DOM is updated
  }
}
